/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line import/extensions -- Jest in this package only resolves SVG assets with their extension.
import folderEmptyStateIconUrl from './assets/folder_empty_state.svg';
import messages from './messages';

const styles = cssMap({
	emptyState: {
		display: 'flex',
		justifyContent: 'center',
		minHeight: '360px',
		marginTop: token('space.100'),
		paddingBlock: token('space.800'),
		paddingInline: token('space.400'),
		gap: token('space.200'),
		alignItems: 'center',
		textAlign: 'center',
		borderWidth: token('border.width'),
		borderStyle: 'dashed',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
	},
	fullscreenEmptyState: {
		marginTop: token('space.300'),
	},
	sidebarEmptyState: {
		marginInlineStart: token('space.300'),
		marginInlineEnd: token('space.300'),
	},
	emptyStateCopy: {
		maxWidth: '560px',
	},
	emptyStateActions: {
		justifyContent: 'center',
		flexWrap: 'wrap',
	},
});

type NoSpacesEmptyStateProps = {
	isFullscreenLayout: boolean;
	onCreateSpace: () => void;
	onViewRecommendedSpaces?: () => void;
};

export const NoSpacesEmptyState = ({
	isFullscreenLayout,
	onCreateSpace,
	onViewRecommendedSpaces,
}: NoSpacesEmptyStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex
			direction="column"
			xcss={cx(
				styles.emptyState,
				isFullscreenLayout ? styles.fullscreenEmptyState : styles.sidebarEmptyState,
			)}
		>
			<Stack alignInline="center" space="space.050">
				<img
					src={folderEmptyStateIconUrl}
					alt=""
					aria-hidden="true"
					width={100}
					height={100}
					data-testid="rovo-spaces-browse-spaces-fullscreen-empty-state-icon"
				/>
				<Stack alignInline="center" space="space.100">
					<Heading size="small">{formatMessage(messages.emptyStateTitle)}</Heading>
					<Box xcss={styles.emptyStateCopy}>
						<Text size="medium" color="color.text.subtlest">
							{formatMessage(messages.emptyStateDescription)}
						</Text>
					</Box>
				</Stack>
			</Stack>
			<Inline xcss={styles.emptyStateActions} space="space.100">
				{onViewRecommendedSpaces && (
					<Button
						appearance="primary"
						spacing="default"
						onClick={onViewRecommendedSpaces}
						testId="rovo-spaces-browse-spaces-fullscreen-view-recommended-spaces-button"
					>
						{formatMessage(messages.viewRecommendedSpacesButtonLabel)}
					</Button>
				)}
				<Button
					appearance="default"
					spacing="default"
					onClick={onCreateSpace}
					testId="rovo-spaces-browse-spaces-fullscreen-empty-create-space-button"
				>
					{formatMessage(messages.createNewSpaceButtonLabel)}
				</Button>
			</Inline>
		</Flex>
	);
};
